import { useSessionKey } from "@auth/sessionStore";
import type { CaptureNode } from "@capture/captureClient";
import { STATUS_WORD, statusForNode } from "@capture/captureNodeStatus";
import { useCaptureNodes } from "@capture/captureQueries";
import {
  EmptyNote,
  LabelTape,
  LedgerBody,
  LedgerCell,
  LedgerHead,
  LedgerHeadCell,
  LedgerRow,
  LedgerTable,
  Swipe,
} from "@scribenote/brian-design-system";
import { formatDateTime, formatRelativeTimeMs } from "@utils/format-date";

const NodeRow = ({ node }: { node: CaptureNode }) => {
  const { label, tone } = STATUS_WORD[statusForNode(node)];
  const lastSeenMs = new Date(node.lastSeen).getTime();
  return (
    <LedgerRow data-testid={`capture-node-row-${node.uuid}`}>
      <LedgerCell>
        {node.roomName ? (
          // Room names are free text; truncate rather than widen the sheet.
          <LabelTape className="max-w-[12rem] truncate align-middle">
            {node.roomName}
          </LabelTape>
        ) : (
          <span className="text-annotation">Unassigned</span>
        )}
      </LedgerCell>
      <LedgerCell mono className="text-annotation">
        {node.instanceId}
      </LedgerCell>
      <LedgerCell className="text-sm">
        {tone ? (
          <Swipe tone={tone} data-testid={`capture-node-status-${node.uuid}`}>
            {label}
          </Swipe>
        ) : (
          <span
            data-testid={`capture-node-status-${node.uuid}`}
            className="text-annotation"
          >
            {label}
          </span>
        )}
      </LedgerCell>
      <LedgerCell
        mono
        className="whitespace-nowrap text-annotation"
        title={formatDateTime(node.lastSeen)}
      >
        {Number.isNaN(lastSeenMs) ? "-" : formatRelativeTimeMs(lastSeenMs)}
      </LedgerCell>
    </LedgerRow>
  );
};

export const CaptureNodesList = () => {
  const sessionKey = useSessionKey();
  const { data: nodes } = useCaptureNodes(sessionKey);

  if (nodes.length === 0)
    return (
      <EmptyNote>
        I haven&rsquo;t heard from any capture devices yet. One shows up here
        once it&rsquo;s set up in the clinic and starts reporting.
      </EmptyNote>
    );

  const sorted = [...nodes].sort(
    (a, b) =>
      a.roomName.localeCompare(b.roomName) ||
      a.instanceId.localeCompare(b.instanceId),
  );

  return (
    <LedgerTable>
      <LedgerHead>
        <tr>
          <LedgerHeadCell>Room</LedgerHeadCell>
          <LedgerHeadCell>Instance</LedgerHeadCell>
          <LedgerHeadCell>Status</LedgerHeadCell>
          <LedgerHeadCell>Last seen</LedgerHeadCell>
        </tr>
      </LedgerHead>
      <LedgerBody>
        {sorted.map((node) => (
          <NodeRow key={node.uuid} node={node} />
        ))}
      </LedgerBody>
    </LedgerTable>
  );
};
